import { midYaw } from './mathUtils';

export function resolveLink(linkOrObj, language, defaultLang = 'en') {
  if (!linkOrObj) return null;
  if (typeof linkOrObj === 'string') return linkOrObj;
  if (linkOrObj[language]) return linkOrObj[language];
  if (linkOrObj[defaultLang]) return linkOrObj[defaultLang];
  return Object.values(linkOrObj)[0] || null;
}

// Arrival text of a room in `language`. `room.messages` is the per-language
// map from avatar_json ({fr: {text, audio}} or {fr: "text"}); `room.message`
// is the default-language text (the only one in older configs). Tries the
// exact code, its base ("fr-ca" → "fr"), then the default language.
export function resolveRoomText(room, language, defaultLang = 'en') {
  if (!room) return '';
  const map = room.messages;
  if (map && typeof map === 'object') {
    const text = v => (typeof v === 'string' ? v : (v && v.text) || '');
    const lang = String(language || '').toLowerCase();
    const hit = [lang, lang.split(/[-_]/)[0], defaultLang].map(k => text(map[k])).find(Boolean);
    if (hit) return hit;
  }
  return typeof room.message === 'string' ? room.message : '';
}

// What the guide says first when a (non-museum) session opens: the room's
// message through the video avatar, else the room's recorded audio, else a
// one-sentence greeting from the model — never silence.
export function pickOpeningIntro(room, { avatar = false, language = 'en', defaultLang = 'en' } = {}) {
  const text = resolveRoomText(room, language, defaultLang);
  if (avatar && text) return { kind: 'avatar-message', text };
  const link = !avatar && room ? resolveLink(room.link, language, defaultLang) : null;
  if (link) return { kind: 'room-audio', link };
  return { kind: 'greet' };
}

export function resolveMsg(msgOrObj, language, defaultLang = 'en') {
  if (!msgOrObj) return '';
  if (typeof msgOrObj === 'string') return msgOrObj;
  if (msgOrObj[language]) return msgOrObj[language];
  if (msgOrObj[defaultLang]) return msgOrObj[defaultLang];
  return Object.values(msgOrObj)[0] || '';
}

function buildScenePoints(scene, language, defaultLang, isCurrent) {
  const sorted = (scene.points || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  if (!sorted.length) return '  (no points)';
  return sorted.map((p, pi) => {
    let yaw = null, pitch = 0;
    if (p.coord) {
      if (p.coord.yaw !== undefined && p.coord.yaw !== null) {
        yaw = p.coord.yaw; pitch = p.coord.pitch || 0;
      } else if (p.coord.start !== undefined) {
        yaw = midYaw(p.coord.start, p.coord.end);
      }
    }
    const rotateCall = yaw !== null ? ` call rotate_view(yaw=${yaw}, pitch=${pitch}),` : '';
    const resolvedLink = resolveLink(p.link, language, defaultLang);
    const msg = resolveMsg(p.msg, language, defaultLang) || '';
    const rotate = isCurrent ? rotateCall : '';
    if (resolvedLink) {
      const note = isCurrent
        ? (msg ? ` [BACKGROUND ONLY — never speak this: "${msg}"]` : '')
        : ``;
      return `  Point ${pi + 1}:${rotate} call play_point_audio(audio_url="${resolvedLink}")${note}`;
    }
    return `  Point ${pi + 1}:${rotate} say: "${msg}"`;
  }).join('\n');
}

/**
 * Builds a compact exhibit context block for the current scene.
 * Used to inject background knowledge into the agent when the visitor asks a question.
 * Contains only exhibit name, description, and background notes — no tool-call instructions.
 */
export function buildCurrentSceneContext(guide = [], currentSceneId = null, currentRoom = null, language = 'en', defaultLang = 'en') {
  const guideScene = currentSceneId
    ? guide.find(s => String(s.id) === String(currentSceneId))
    : guide.slice().sort((a, b) => (a.position || 0) - (b.position || 0))[0];

  if (!guideScene && !currentRoom) return '';

  const name = currentRoom?.title || guideScene?.name || currentSceneId;
  const desc = currentRoom?.description || '';
  let block = `Exhibit: "${name}"`;
  if (desc) block += `\nDescription: ${desc}`;

  const points = [...(guideScene?.points || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
  const notes = points.map(p => resolveMsg(p.msg, language, defaultLang)).filter(Boolean);
  if (notes.length) {
    block += `\nBackground (for answering only — never read aloud):\n` + notes.map(n => `- ${n}`).join('\n');
  }

  return block;
}

export function buildGuideScript(guide = [], currentSceneId = null, currentRoom = null, language = 'en', defaultLang = 'en') {
  const sorted = guide.slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const currentIdx = currentSceneId ? sorted.findIndex(s => String(s.id) === String(currentSceneId)) : -1;

  const parts = [];

  if (currentRoom) {
    const rid = currentRoom.id;
    let block = `CURRENT SCENE: "${currentRoom.title || rid}" (R_ID: ${rid})`;
    if (currentRoom.description) {
      block += `\nROOM DESCRIPTION: ${currentRoom.description}`;
    }
    if (currentRoom.hotspots && currentRoom.hotspots.length) {
      block += `\nHOTSPOTS:\n` + currentRoom.hotspots
        .map(h => `-"${h.title || h.hotspotID}" (H_ID: ${h.hotspotID})${h.systemPrompt ? ': ' + h.systemPrompt : ''}`)
        .join('\n');
    }
    const currentGuideScene = guide.find(s => s.id === rid);
    if (currentGuideScene?.points?.length) {
      block += '\n' + buildScenePoints(currentGuideScene, language, defaultLang, true);
    }
    parts.push(block);
  } else if (currentIdx !== -1) {
    const scene = sorted[currentIdx];
    parts.push(`CURRENT SCENE: "${scene.name}" (R_ID: ${scene.id})\n` + buildScenePoints(scene, language, defaultLang, true));
  }

  if (currentIdx !== -1) {
    if (currentIdx < sorted.length - 1) {
      const next = sorted[currentIdx + 1];
      parts.push(`NEXT SCENE: "${next.name}" (R_ID: ${next.id})\n` + buildScenePoints(next, language, defaultLang, false));
    }
  } else if (sorted.length) {
    // No current scene known — default to first scene as current
    parts.push(`CURRENT SCENE: "${sorted[0].name}" (R_ID: ${sorted[0].id})\n` + buildScenePoints(sorted[0], language, defaultLang, true));
    if (sorted[1]) {
      parts.push(`NEXT SCENE: "${sorted[1].name}" (R_ID: ${sorted[1].id})\n` + buildScenePoints(sorted[1], language, defaultLang, false));
    }
  }

  return parts.join('\n\n');
}
